import React from 'react';
import { arrayOf, shape, func, bool } from 'prop-types';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import { fetchSelectProjects, clearSelectProjects } from '#action/project/project-select';
import { toMeta } from '#helper/rhf-meta';
import { ParentProjectField } from '#component/project/form/project/field-styles';

/**
 * Renders parent project select field.
 * @returns {React.FC}
 */

const ParentProjectSelectionField = props => {
  const {
    arrayProjects,
    formData,
    change,
    editable
  } = props;

  const dispatch = useDispatch();
  const { control, formState } = useFormContext();
  const account = useSelector(s => s.account, shallowEqual);
  const { projects, status } = useSelector(s => s.projectSelect, shallowEqual);

  const handleProjectsFetch = e => dispatch(fetchSelectProjects(e.target.value));

  const clearProjects = () => {
    dispatch(clearSelectProjects());
  };

  const fetchProjects = () => {
    dispatch(fetchSelectProjects());
  };

  const handleParentProjectChange = e => {
    change('parentId', e.value);

    const parentBeginDate = e.beginDate;
    const parentExpirationDate = e.expirationDate;
    const { beginDate } = formData;

    if (beginDate < parentBeginDate || !Date.parse(beginDate)) {
      change('beginDate', parentBeginDate);
    }

    if (parentExpirationDate) {
      change('expirationDate', parentExpirationDate);
      change('isExpirationDate', '1');
    } else {
      change('expirationDate', undefined);
      change('isExpirationDate', '0');
    }
  };

  const projectToSelect = p => p?.map(i => ({
    label: `${i.title}`,
    value: i.id,
    ...i
  }));

  const projectsToSelect = projectToSelect(projects);
  const parent = arrayProjects.filter(p => formData.parentId === p.id);
  const parentProjectToSelect = projectToSelect(parent);

  return (
    <Controller
      name="parentId"
      control={control}
      render={({ field, fieldState }) => (
        <ParentProjectField
          name={field.name}
          meta={toMeta(fieldState, formState)}
          label="Parent project"
          placeholder="Parent project"
          disabled={!editable}
          margin="normal"
          variant="outlined"
          data={projectsToSelect}
          setData={handleParentProjectChange}
          defaultData={parentProjectToSelect}
          account={account}
          fetchProjects={handleProjectsFetch}
          clearProjects={clearProjects}
          loading={['loading'].includes(status)}
          textFieldOnFocus={fetchProjects}
          showDateRange={true}
        />
      )}
    />
  );
};

ParentProjectSelectionField.propTypes = {
  arrayProjects: arrayOf(shape({})),
  formData: shape({}).isRequired,
  change: func.isRequired,
  editable: bool
};

export default ParentProjectSelectionField;
